import React, { useState } from "react";
import { GlobalService } from "./../../shared";
import Card from "../../../components/ui/card";
import { CardHeader, CardTitle, CardContent } from "../../../components/ui/card";
import { ArrowLeft, Mail, CircleAlert } from "lucide-react";
import { motion } from "framer-motion";
import { ForgotPassword, useAppDispatch } from "../../../redux";
import { ForgotType } from "../../shared/model/AuthInterfaces";
import CommonLoginLeftPart from "./CommonLoginLeftPart";
import { Link, useNavigate } from "react-router-dom";
import { selectLoaderValue } from "../../../redux/Slices/loadingSlice";
import { useSelector } from "react-redux";
import Spinner from "../../shared/components/Spinner";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../../../components/LanguageSelector";
const ForgotPasswordComponent = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const isLoading = useSelector(selectLoaderValue);
    const [formData, setFormData] = useState({
        email: "",
    });
    const [errors, setErrors] = useState<ForgotType>({});
    const navigate = useNavigate();

    const validate = () => {
        const newErrors: ForgotType = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!formData.email) {
            newErrors.email = t("forgotPassword.emailRequired");
        } else if (!emailRegex.test(formData.email)) {
            newErrors.email = t("forgotPassword.invalidEmail");
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async e => {
        if (!isLoading) {
            e.preventDefault();
            if (validate()) {
                try {
                    await dispatch(ForgotPassword(formData)).unwrap();
                    navigate("/auth/login");
                } catch (err) {
                    throw err;
                }
            }
        } else {
            GlobalService.showToast("info", t("forgotPassword.pleaseWait"), t("forgotPassword.processingRequest"));
        }
    };
    const handleChange = e => {
        const { name, type, checked, value } = e.target;
        setFormData({
            ...formData,
            [name]: type === "checkbox" ? checked : value,
        });
    };
    return (
        <>
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-gray-100 flex flex-col p-4">
                {/* Navigation Header */}
                <div className="max-w-7xl mx-auto w-full py-6 px-4">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="flex items-center justify-between">
                        <Link to={"/"}
                            className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors">
                            <ArrowLeft className="w-5 h-5" />
                            <span>{t("forgotPassword.backToHome")}</span>
                        </Link>
                        <LanguageSelector />
                    </motion.div>
                </div>

                {/* Main Content */}
                <div className="flex-1 flex items-center justify-center">
                    <div className="w-full max-w-4xl grid md:grid-cols-2 gap-8 items-center p-4">
                        {/* Left Side - Information */}
                        <CommonLoginLeftPart />
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 }}>
                            <Card className="border-0 shadow-2xl bg-white/80 backdrop-blur" color={undefined}>
                                <CardHeader className="pb-6" color={undefined}>
                                    <div className="md:hidden mb-6 flex justify-center">
                                        <div className="text-2xl font-extrabold text-blue-600 flex items-center gap-3">
                                            <img
                                                src="https://assests.netlify.app/assets/images/logo.png"
                                                alt="Kokobeo Logo"
                                                className="w-10 h-10 object-contain"
                                            />
                                            <span>Kokobeo</span>
                                        </div>
                                    </div>
                                    <CardTitle
                                        className="text-2xl font-bold text-center text-gray-900"
                                        color={undefined}>
                                        {t("forgotPassword.title")}
                                    </CardTitle>
                                    <p className="mt-2 text-sm text-gray-600 text-center">
                                        {t("forgotPassword.subtitle")}
                                    </p>
                                </CardHeader>
                                {Object.keys(errors).length > 0 && (
                                    <div className="w-full flex justify-center">
                                        <div className="w-[88%]  bg-red-100 rounded-md px-3 py-2 flex items-center">
                                            <CircleAlert className="text-red-500 w-4 h-4" />
                                            <p className="text-red-600 text-sm pl-2">
                                                {String(Object.values(errors)[0])}
                                            </p>
                                        </div>
                                    </div>
                                )}
                                <CardContent className="space-y-6">
                                    {/* Divider */}
                                    <div className="relative">
                                        <div className="absolute inset-0 flex items-center">
                                            <div className="w-full border-t border-gray-200" />
                                        </div>
                                        <div className="relative flex justify-center text-sm">
                                            <span className="px-4 text-gray-500 bg-white">{t("forgotPassword.orContinueWithEmail")}</span>
                                        </div>
                                    </div>

                                    {/* Email Field */}
                                    <div className="space-y-2">
                                        <label htmlFor="email" className="text-sm font-medium text-gray-700 block">
                                            {t("forgotPassword.email")}
                                        </label>
                                        <div className="relative">
                                            <input
                                                id="email"
                                                name="email"
                                                type="email"
                                                placeholder={t("forgotPassword.emailPlaceholder")}
                                                value={formData.email}
                                                onChange={handleChange}
                                                className="h-12 w-full rounded-lg border border-gray-300 pl-11 pr-4 text-sm 
                        text-gray-800 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500"
                                            />
                                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        </div>
                                    </div>

                                    {/* Login Button */}
                                    <motion.button
                                        onClick={handleSubmit}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        className="w-full h-12 bg-blue-600 text-white rounded-lg hover:bg-blue-700 
                    transition-colors font-medium flex items-center justify-center gap-2">
                                        {isLoading ? (
                                            <>
                                                <Spinner size={5} />
                                                {t("forgotPassword.loading")}
                                            </>
                                        ) : (
                                            t("forgotPassword.submitButton")
                                        )}{" "}
                                    </motion.button>

                                    {/* Registration Link */}
                                    <p className="text-sm text-center text-gray-600">
                                        <Link to={"/auth/login"}>
                                            <motion.button
                                                onClick={() => { }}
                                                whileHover={{ scale: 1.05 }}
                                                className="text-blue-600 hover:text-blue-700 hover:underline font-medium">
                                                {t("forgotPassword.backToLogin")}{" "}
                                            </motion.button>
                                        </Link>
                                    </p>
                                </CardContent>
                            </Card>
                        </motion.div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ForgotPasswordComponent;
